import { Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type JSX, type RefObject } from "react";
import { Link } from "react-router-dom";

import type { SearchHitDTO, SearchSortDTO } from "../api/types.js";
import { AppIcon } from "../components/AppIcon.js";
import { useModalFocus } from "../components/useModalFocus.js";
import { useGlobalSearch } from "./searchQueries.js";

const TYPE_OPTIONS = [
  ["", "Alle Arten"], ["file", "Dateien"], ["folder", "Ordner"],
  ["event", "Termine"], ["message", "Nachrichten"],
] as const;

export function GlobalSearch(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const returnFocus = useRef<HTMLElement | null>(null);
  const restoringFocus = useRef(false);
  const openSearch = (): void => {
    returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setOpen(true);
  };
  const closeSearch = (): void => {
    restoringFocus.current = true;
    setOpen(false);
  };
  return (
    <>
      <form className="hidden sm:block" onSubmit={(event) => { event.preventDefault(); openSearch(); }}>
        <label className="relative block">
          <span className="sr-only">Global suchen</span>
          <Search className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-ink-muted" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} onFocus={() => {
            if (restoringFocus.current) { restoringFocus.current = false; return; }
            openSearch();
          }}
            placeholder="Suchen …" className="w-48 rounded-lg border border-line bg-surface-sunken py-1.5 pl-8 pr-3 text-sm text-ink outline-none focus:border-brand lg:w-64" />
        </label>
      </form>
      <button type="button" aria-label="Global suchen" onClick={openSearch}
        className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken hover:text-ink sm:hidden">
        <Search className="h-5 w-5" />
      </button>
      {open ? <SearchDialog query={query} onQuery={setQuery} onClose={closeSearch} returnFocus={returnFocus} /> : null}
    </>
  );
}

function SearchDialog({ query, onQuery, onClose, returnFocus }: { query: string; onQuery: (value: string) => void; onClose: () => void; returnFocus: RefObject<HTMLElement | null> }): JSX.Element {
  const panel = useModalFocus(onClose, returnFocus);
  const [source, setSource] = useState("");
  const [type, setType] = useState("");
  const [sort, setSort] = useState<SearchSortDTO>("relevance");
  const [offset, setOffset] = useState(0);
  const search = useGlobalSearch({ query, source, type, sort, offset });
  useEffect(() => setOffset(0), [query, source, type, sort]);
  const grouped = useMemo(() => groupHits(search.data?.hits ?? []), [search.data?.hits]);

  // Oben die Notch, unten die Gestenleiste: die Suche liegt über allem und muss
  // beides selbst berücksichtigen.
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-3 pb-[calc(theme(spacing.3)+env(safe-area-inset-bottom))] pt-[calc(8vh+env(safe-area-inset-top))] sm:p-6 sm:pt-[10vh]">
      <button type="button" aria-label="Suche schließen" onClick={onClose} className="absolute inset-0" />
      <div ref={panel} role="dialog" aria-modal="true" aria-label="Globale Suche" tabIndex={-1}
        className="relative flex max-h-[82vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-line bg-surface-raised shadow-2xl">
        <div className="flex items-center gap-2 border-b border-line p-3">
          <Search className="h-5 w-5 shrink-0 text-ink-muted" />
          <input autoFocus value={query} onChange={(event) => onQuery(event.target.value)} placeholder="Apps und Inhalte durchsuchen"
            className="min-w-0 flex-1 bg-transparent text-base text-ink outline-none" />
          <button type="button" onClick={onClose} aria-label="Schließen" className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex flex-wrap gap-2 border-b border-line p-3">
          <select aria-label="App filtern" value={source} onChange={(event) => setSource(event.target.value)} className="rounded-lg border border-line bg-surface-raised px-2 py-1.5 text-sm text-ink">
            <option value="">Alle Apps</option>
            {(search.data?.sources ?? []).map((entry) => <option key={entry.id} value={entry.id}>{entry.label}</option>)}
          </select>
          <select aria-label="Art filtern" value={type} onChange={(event) => setType(event.target.value)} className="rounded-lg border border-line bg-surface-raised px-2 py-1.5 text-sm text-ink">
            {TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select aria-label="Sortierung" value={sort} onChange={(event) => setSort(event.target.value as SearchSortDTO)} className="rounded-lg border border-line bg-surface-raised px-2 py-1.5 text-sm text-ink">
            <option value="relevance">Beste Treffer</option><option value="updated">Zuletzt geändert</option><option value="title">Titel</option>
          </select>
        </div>
        <div className="min-h-40 overflow-y-auto p-3">
          {query.trim().length < 2 ? <p className="p-6 text-center text-sm text-ink-muted">Mindestens zwei Zeichen eingeben.</p> : null}
          {search.isPending ? <p className="p-6 text-center text-sm text-ink-muted">Suche läuft …</p> : null}
          {search.error ? <p role="alert" className="p-6 text-center text-sm text-danger">{search.error.message}</p> : null}
          {search.data && search.data.hits.length === 0 ? <p className="p-6 text-center text-sm text-ink-muted">Keine passenden Inhalte gefunden.</p> : null}
          {[...grouped.entries()].map(([group, hits]) => (
            <section key={group} className="mb-4"><h2 className="mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">{sourceLabel(search.data?.sources ?? [], group)}</h2>
              <ul>{hits.map((hit) => <SearchResult key={hit.id} hit={hit} onOpen={onClose} />)}</ul>
            </section>
          ))}
        </div>
        {search.data && (offset > 0 || search.data.nextOffset !== null) ? <div className="flex justify-between border-t border-line p-3">
          <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 20))} className="rounded-lg px-3 py-1.5 text-sm text-brand disabled:opacity-40">Zurück</button>
          <button type="button" disabled={search.data.nextOffset === null} onClick={() => setOffset(search.data?.nextOffset ?? offset)} className="rounded-lg px-3 py-1.5 text-sm text-brand disabled:opacity-40">Weiter</button>
        </div> : null}
      </div>
    </div>
  );
}

function SearchResult({ hit, onOpen }: { hit: SearchHitDTO; onOpen: () => void }): JSX.Element {
  return <li><Link to={hit.url} onClick={onOpen} className="flex min-w-0 gap-3 rounded-xl p-2 hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand">
    <span className="mt-0.5 text-brand"><AppIcon name={hit.icon ?? "search"} className="h-5 w-5" /></span>
    <span className="min-w-0"><span className="block truncate text-sm font-medium text-ink">{hit.title}</span>
      {hit.snippet ? <span className="mt-0.5 line-clamp-2 text-xs text-ink-muted"><Highlighted text={hit.snippet} /></span> : null}
    </span>
  </Link></li>;
}

function Highlighted({ text }: { text: string }): JSX.Element {
  const parts = text.split(/([⟦⟧])/); let marked = false;
  return <>{parts.map((part, index) => { if (part === "⟦") { marked = true; return null; } if (part === "⟧") { marked = false; return null; } return marked ? <mark key={index} className="bg-brand/15 text-ink">{part}</mark> : part; })}</>;
}

function groupHits(hits: readonly SearchHitDTO[]): Map<string, SearchHitDTO[]> {
  const groups = new Map<string, SearchHitDTO[]>();
  for (const hit of hits) groups.set(hit.source, [...(groups.get(hit.source) ?? []), hit]);
  return groups;
}

function sourceLabel(sources: readonly { id: string; label: string }[], id: string): string {
  return sources.find((source) => source.id === id)?.label ?? id;
}
